import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import CardFace from "./CardFace"
import { CARD_PEEK_COPY, cardFace } from "@/content/cards"
import type { CardId } from "@/types"
import type { ReactNode } from "react"
import styles from "@/styles/components/CardPeek.module.css"

/*
 * Reading a card that is only a token.
 *
 * `CardThumb` is 34–56px of family colour and a number, and the shortlist's
 * `CardFace` is a 132px thumbnail whose statement is clamped to four lines of
 * 11px type. Both are deliberate — at those sizes the statement is noise — but
 * they leave the room with cards on screen it cannot actually read, and the
 * `title` attribute is a poor answer: it is slow, it is unstyled, and it cannot
 * show the back.
 *
 * So: hover a small card, an eye appears; hover the eye, the card opens beside
 * it at full size with its flip. It stays up while the pointer is over either
 * the thumbnail or the panel, which is what lets someone cross into the panel
 * and turn the card over.
 *
 * It wraps a trigger rather than living inside `CardThumb` because a thumb is
 * often already interactive — a drag handle on the board, a tab in the deep-dive
 * strip — and a button inside a button is invalid. Wrapping the whole control
 * keeps the eye a sibling of it.
 *
 * `onRemove` puts a second badge in the opposite corner, and it is here for the
 * same reason the eye is: it is the hover affordance a small card has room for.
 * Where a card can be taken back out — the hand slots — the alternative was
 * making the whole tile a button that discards, which is a card you cannot point
 * at without losing it, on the one control people reach for to read it. A named
 * target in a corner says what it does and leaves the rest of the tile inert.
 */

/*
 * How long the panel survives the pointer leaving. The panel's own padding is
 * the bridge across the visual gap, so this only has to cover the frame or two
 * the pointer can spend registered against neither box.
 */
const CLOSE_MS = 120

/** Keeps the panel off the viewport edge. */
const EDGE = 8

interface CardPeekProps {
  cardId: CardId
  /** The small card. Anything: a `CardThumb`, a `CardFace`, a tab holding one. */
  children: ReactNode
  /** Adds the trash badge in the opposite corner. Omit and there is no discard. */
  onRemove?: () => void
  /** What the trash does, as it is announced. Required alongside `onRemove`. */
  removeLabel?: string
  className?: string
}

export default function CardPeek({
  cardId,
  children,
  onRemove,
  removeLabel,
  className,
}: CardPeekProps) {
  const [open, setOpen] = useState(false)
  const [side, setSide] = useState<"front" | "back">("front")
  const [spot, setSpot] = useState<{ top: number; left: number } | null>(null)
  /*
   * Opened from the keyboard, where there is no pointer to keep it up and no
   * way to reach the flip button: the panel is portalled to `body`, so it is
   * nowhere near the eye in tab order. Focus goes into it instead.
   */
  const [byKeyboard, setByKeyboard] = useState(false)

  const wrap = useRef<HTMLSpanElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const eye = useRef<HTMLButtonElement>(null)
  const timer = useRef<number | null>(null)

  const hold = useCallback(() => {
    if (timer.current === null) return
    window.clearTimeout(timer.current)
    timer.current = null
  }, [])

  // Closing resets the face and the placement together: a card opened again
  // should read as the card, not as whatever the last look left turned over, and
  // a stale spot would place the panel against a trigger that has since moved.
  const close = useCallback(() => {
    // The panel is about to be unmounted; focus inside it would fall to the body.
    if (panel.current?.contains(document.activeElement)) eye.current?.focus()
    setOpen(false)
    setSide("front")
    setSpot(null)
    setByKeyboard(false)
  }, [])

  /*
   * Asking `:hover` at the moment the timer fires, rather than tracking enter
   * and leave into a flag: the pointer moving from the thumbnail to the panel
   * fires one leave and one enter whose order is not guaranteed — the panel is
   * portalled, so the two are unrelated in the DOM — and a flag written by
   * whichever landed second closes a panel the pointer is sitting on.
   *
   * Focus is asked the same way and for the same reason: tabbing from the eye
   * into the panel blurs one and focuses the other, and the answer settles by
   * the time the timer runs.
   */
  const release = useCallback(() => {
    hold()
    timer.current = window.setTimeout(() => {
      timer.current = null
      if (wrap.current?.matches(":hover") || panel.current?.matches(":hover")) return
      if (
        wrap.current?.contains(document.activeElement) ||
        panel.current?.contains(document.activeElement)
      ) {
        return
      }
      close()
    }, CLOSE_MS)
  }, [close, hold])

  useEffect(() => () => hold(), [hold])

  // The flip button is the only thing in the panel worth reaching, and it is
  // the first control in it.
  useEffect(() => {
    if (!open || !byKeyboard) return
    panel.current?.querySelector<HTMLButtonElement>("button")?.focus()
  }, [byKeyboard, open])

  /*
   * Placed against the trigger's box in viewport coordinates, because the panel
   * is portalled to `body` to escape the clipping the small cards live inside —
   * the deal's `overflow-x: clip`, the board's quadrants, the back's scrolling
   * facts. Measured rather than assumed: the card's width is a `clamp()` on vw.
   */
  useLayoutEffect(() => {
    if (!open) return

    const place = () => {
      const trigger = wrap.current?.getBoundingClientRect()
      const box = panel.current?.getBoundingClientRect()
      if (!trigger || !box) return

      // Flush against the thumbnail, not offset from it: the gap the eye sees is
      // the panel's padding, and padding is hoverable, so the pointer never
      // crosses dead space on its way in.
      let left = trigger.right
      if (left + box.width > window.innerWidth - EDGE) left = trigger.left - box.width
      left = Math.max(EDGE, Math.min(left, window.innerWidth - box.width - EDGE))

      let top = trigger.top + trigger.height / 2 - box.height / 2
      top = Math.max(EDGE, Math.min(top, window.innerHeight - box.height - EDGE))

      setSpot((current) =>
        current && current.top === top && current.left === left ? current : { top, left }
      )
    }

    place()
    // Capturing, so a scroll in any ancestor moves the panel and not only the page.
    window.addEventListener("scroll", place, true)
    window.addEventListener("resize", place)
    return () => {
      window.removeEventListener("scroll", place, true)
      window.removeEventListener("resize", place)
    }
  }, [open])

  // Escape for the keyboard, a press elsewhere for touch — where there is no
  // hover at all and the eye is opened by tapping it.
  useEffect(() => {
    if (!open) return

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      close()
      eye.current?.focus()
    }
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (wrap.current?.contains(target) || panel.current?.contains(target)) return
      close()
    }

    document.addEventListener("keydown", onKey)
    document.addEventListener("pointerdown", onDown)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("pointerdown", onDown)
    }
  }, [close, open])

  const face = cardFace(cardId)
  const label = CARD_PEEK_COPY.open(face?.name ?? cardId)

  return (
    <span
      ref={wrap}
      className={className ? `${styles.wrap} ${className}` : styles.wrap}
      data-open={open || undefined}
      onPointerEnter={hold}
      onPointerLeave={release}
    >
      {children}

      <button
        type="button"
        ref={eye}
        className={styles.eye}
        title={label}
        aria-label={label}
        aria-expanded={open}
        draggable={false}
        /* The thumbnail underneath is often a drag handle or a tab that moves
           every screen in the room; neither should hear this press. */
        onPointerDown={(event) => event.stopPropagation()}
        onPointerEnter={() => {
          hold()
          setOpen(true)
        }}
        onFocus={() => {
          hold()
          setOpen(true)
        }}
        onBlur={release}
        /* The tap fallback: on a touch screen there is no hover to open it with,
           so the eye opens on tap. It must NOT close what the pointer just
           opened — a mouse fires `pointerenter` before `click`, and closing here
           shut the panel the click was meant to read, with the pointer still on
           the eye so no second `pointerenter` would reopen it. A hovered eye
           closes on leave instead.

           `detail === 0` is Enter or Space: no pointer opened this one, so the
           panel has to be handed the focus or the flip button is unreachable. */
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          const fromKeyboard = event.detail === 0
          if (open && !fromKeyboard && !eye.current?.matches(":hover")) close()
          else {
            hold()
            setByKeyboard(fromKeyboard)
            setOpen(true)
          }
        }}
      >
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M1.4 8S4 3.6 8 3.6 14.6 8 14.6 8 12 12.4 8 12.4 1.4 8 1.4 8Z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          <circle cx="8" cy="8" r="1.9" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      </button>

      {onRemove && removeLabel ? (
        <button
          type="button"
          className={styles.discard}
          title={removeLabel}
          aria-label={removeLabel}
          draggable={false}
          /* Same reason as the eye: the thumbnail underneath may be a drag
             handle, and this press is not the start of a drag. */
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            /* No spinner, by the same exemption the rest of the round takes: the
               hand is optimistic, so the card leaves the row on the press and
               the row IS the feedback. */
            onRemove()
          }}
        >
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M6.2 3.4V2.3h3.6v1.1M3 3.4h10M4.7 3.4l.5 9.4h5.6l.5-9.4"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}

      {open
        ? createPortal(
            <div
              ref={panel}
              /* Rendered before it has been measured, so it has a size to
                 measure — but placed at 0,0 until then, which must not be seen.
                 The measure runs in a layout effect, so it is placed before the
                 browser paints and `measuring` never survives a frame. */
              className={spot ? styles.panel : `${styles.panel} ${styles.measuring}`}
              style={{ top: spot?.top ?? 0, left: spot?.left ?? 0 }}
              onPointerEnter={hold}
              onPointerLeave={release}
              onFocus={hold}
              onBlur={release}
            >
              <CardFace
                cardId={cardId}
                side={side}
                size="lg"
                onFlip={() => setSide((value) => (value === "front" ? "back" : "front"))}
              />
            </div>,
            document.body
          )
        : null}
    </span>
  )
}
